import { useContext, useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AuthContext } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Search,
  Settings,
  LogOut,
  ChevronDown,
  Briefcase,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/prospects", label: "Prospects", icon: Search },
  { to: "/pipeline", label: "Pipeline", icon: Briefcase },
  { to: "/settings", label: "Settings", icon: Settings },
];

function Sidebar() {
  const location = useLocation();
  const { API } = useContext(AuthContext);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    fetch(`${API}/leads/unread-count`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : { count: 0 }))
      .then((d) => { if (active) setUnread(d.count || 0); })
      .catch(() => {});
    return () => { active = false; };
  }, [API, location.pathname]);

  return (
    <aside
      data-testid="sidebar"
      className="fixed left-0 top-0 bottom-0 w-[220px] bg-indigo-900 text-white flex flex-col noise-bg z-40"
    >
      {/* Logo */}
      <div className="relative z-10 px-5 py-5">
        <div className="inline-flex rounded-lg px-3 py-2 shadow-sm" style={{ backgroundColor: "#ffffff" }}>
          <img src="/gm-pulse-logo.png" alt="GM Pulse" data-testid="sidebar-logo" className="h-6 w-auto" />
        </div>
        <p className="text-[10px] text-slate-500 mt-2 pl-0.5 tracking-wide">Great Marketing LLC</p>
      </div>

      {/* Navigation */}
      <nav className="relative z-10 flex-1 px-3 mt-2 space-y-0.5">
        <p className="px-3 mb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-600">
          Menu
        </p>
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
          return (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-2.5 px-3 py-2 rounded-md text-[13px] font-medium transition-all duration-150 ${
                isActive
                  ? "bg-indigo-600/20 text-white border-l-2 border-indigo-500"
                  : "text-slate-400 hover:text-white hover:bg-white/5"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
              {item.to === "/pipeline" && unread > 0 && (
                <span data-testid="nav-unread-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">
                  {unread}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Version footer */}
      <div className="relative z-10 px-5 py-3 text-[10px] text-slate-600">
        v0.9.1
      </div>
    </aside>
  );
}

function Header() {
  const { user, logout } = useContext(AuthContext);
  const initials = user?.name
    ? user.name.split(" ").map((n) => n[0]).join("").toUpperCase()
    : "U";

  return (
    <header
      data-testid="app-header"
      className="glass-header sticky top-0 z-30 h-12 flex items-center justify-end gap-1 px-5"
    >
      <ThemeToggle />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            data-testid="user-menu-trigger"
            className="flex items-center gap-2 h-8 px-2 hover:bg-slate-100"
          >
            <Avatar className="w-6 h-6">
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="text-[10px] bg-indigo-100 text-indigo-700">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium hidden sm:inline">{user?.name}</span>
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <div className="px-2 py-1.5 text-xs text-muted-foreground">{user?.email}</div>
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild>
            <NavLink to="/settings" data-testid="menu-settings" className="cursor-pointer">
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </NavLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            data-testid="menu-logout"
            onClick={logout}
            className="text-destructive cursor-pointer"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}

export default function Layout({ children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <div className="ml-[220px]">
        <Header />
        <main className="px-5 py-4">{children}</main>
      </div>
    </div>
  );
}
